/* ----------------------------------------------------------------------------
 * turnEntity — Phase B of the voice turn/audio redesign: ONE explicit
 * conversation-turn entity per docs/brain/product/voice-turn-audio-redesign-
 * scope-2026-07-27.md §3, consolidating (not reinventing) the turn-routing
 * state that used to live as independent parallel ref-flags on
 * useVoiceController.ts (clarifyCtxRef, publishConfirmCtxRef, handsFreeTurnRef,
 * hasWokenRef, closedAtRef). CLARIFY and PUBLISH_CONFIRM are SUB-STATES this
 * entity carries (`subState`), not siblings; classifyFinal() is the ONE
 * per-state routing dispatcher onFinal now defers to instead of an inline,
 * ordering-sensitive if-chain.
 *
 * Pure, DOM-free, browser-free, network-free (mirrors wakeMachine.ts's
 * stepWakeFlow design) so the whole routing decision is unit-testable under
 * Node --test with no react, no mic, no TTS. useVoiceController.ts is the
 * thin glue: it holds ONE ConversationTurn instance (in a ref, since its
 * fields mutate synchronously exactly like the refs it replaces) and calls
 * its methods instead of reading/writing five separate refs.
 *
 * SCOPE (Phase B, locked): fold clarifyCtxRef/publishConfirmCtxRef into ONE
 * `subState` field; give handleTurnCloseWake a SINGLE close() transition that
 * resets hasWoken/handsFreeTurn/subState/cooldown together; centralise
 * onFinal's ordering-sensitive guard chain into classifyFinal(). Explicitly
 * OUT of Phase B (deferred, not silently absorbed): useHandsFree.ts's
 * turnInFlightRef / clarifyAnswerActiveRef (a different consumer, the
 * WakeEffect/CaptureWindow layer across the bridge boundary — folding those in
 * would require threading this entity across the HandsFreeBridge interface, a
 * bigger architectural change than "consolidation" — see the Phase B build
 * note).
 * -------------------------------------------------------------------------- */

import type { BriefingModule } from './wakeMachine.ts';
import { isTurnClose, isStrongStop, detectModuleAsk, isBriefingIntent, isFollowUpDecline } from './wakeMachine.ts';
import { finalTranscriptAction } from '../state/machine.ts';
import { POST_CLOSE_COOLDOWN_MS, POST_ANSWER_SCORE_GRACE_MS } from './handsFreeConfig.ts';
import type { PublishConfirmDraft } from '../actions/publishConfirmModel.ts';
import { detectBatchPublishAsk, type BatchPublishAsk, type BatchChannel, type BatchReadbackMode } from '../actions/batchPublishModel.ts';
import { detectSuggestWriteAsk, type SuggestWriteAsk } from '../actions/suggestWriteModel.ts';
import { detectPendingListAsk, detectRecommendAsk, type RecommendAsk } from '../actions/recommendModel.ts';

/** CLARIFY sub-state: the offered module menu + re-ask counter + the ORIGINAL
 *  wake utterance (needed to re-issue the scoped assist call once an answer
 *  resolves). Replaces clarifyCtxRef's payload byte-for-byte. */
export interface ClarifySubState {
  kind: 'clarify';
  modules: BriefingModule[];
  reAskCount: number;
  utterance: string;
}

/** PUBLISH_CONFIRM sub-state: the prepared draft + re-ask counter. Replaces
 *  publishConfirmCtxRef's payload byte-for-byte. */
export interface PublishConfirmSubState {
  kind: 'publish_confirm';
  draft: PublishConfirmDraft;
  reAskCount: number;
}

/** A turn is EITHER in no sub-state (a plain command/follow-up turn) OR in
 *  EXACTLY ONE of these two closed-set sub-states — never both, never a pair
 *  of parallel flags that can independently drift out of sync (the defect
 *  class this replaces). */
/** BATCH_PUBLISH sub-state (Aura publish P1, owner decision 10-10-2026): the
 *  cards on screen + re-ask counter while Aura waits for "publish" / "draft". */
export interface BatchPublishSubState {
  kind: 'batch_publish';
  targetId: number;
  draftIds: number[];
  channel: BatchChannel;
  reAskCount: number;
  /** set when Aura's spoken read-back already named the action, site and count
   *  ("Publish 'X' to booksesh.com?"): a bare "yes" then confirms THIS mode. */
  readbackMode: BatchReadbackMode | null;
}

/** SUGGEST_WRITE sub-state (Aura P2, owner GO 10-10-2026): the 3 suggestion
 *  cards on screen + re-ask counter while Aura waits for "yes" / "write them". */
export interface SuggestWriteSubState {
  kind: 'suggest_write';
  targetId: number;
  channel: BatchChannel;
  reAskCount: number;
  /** the ONE card the open read-back names ("Write a blog for 'X' on <site>?"), or
   *  null when the question covers all the suggestion cards ("Shall I write them?"). */
  pick: number | null;
  /** "Which ones, or shall I write all?" is open: the next answer selects cards. */
  selecting?: boolean;
}

/** RECOMMEND sub-state (conversational-actions, owner GO 12-10-2026 20:30 UTC): Aura's
 *  numbered recommend list is on screen and Aura asked "…all now, or tell me the number or
 *  name?" -- the next answer selects ("2", "1 and 3", title words, "all", "no"). */
export interface RecommendSubState {
  kind: 'recommend';
  channel: BatchChannel;
  reAskCount: number;
}

export type TurnSubState = ClarifySubState | PublishConfirmSubState | BatchPublishSubState | SuggestWriteSubState | RecommendSubState | null;

/** The per-state routing decision for one STT onFinal result. Centralises the
 *  ordering that used to be an inline if-chain inside the STT onFinal closure
 *  (voice-wave-4 D1/close-reask root-cause class) into ONE pure, testable
 *  dispatcher keyed FIRST on the turn's current sub-state, matching §3's
 *  "onFinal becomes per-state routing" requirement. The glue (onFinal) still
 *  owns epoch/staleness (an IO-timing concern, not turn-content) and the
 *  actual side effects (dispatch/speak/assist) — this function ONLY decides
 *  which handler the transcript belongs to. */
export type FinalRoute =
  | { kind: 'publish_confirm_answer' }
  | { kind: 'batch_publish_answer' }
  | { kind: 'batch_publish_ask'; ask: BatchPublishAsk }
  | { kind: 'suggest_write_answer' }
  | { kind: 'suggest_write_ask'; ask: SuggestWriteAsk }
  | { kind: 'recommend_answer' }
  | { kind: 'recommend_ask'; ask: RecommendAsk }
  | { kind: 'turn_close' }
  | { kind: 'clarify_answer' }
  | { kind: 'clarify_no_answer_fallback' }
  | { kind: 'fragment_reprompt' }
  | { kind: 'quiet_close' }
  | { kind: 'module_ask'; keys: string[] }
  | { kind: 'briefing' }
  | { kind: 'normal' };

/** ONE conversation-turn entity (§3): owns the sub-state that used to be two
 *  independently-mutated parallel refs, the hands-free-turn flag, the
 *  once-per-session "woken" flag, and the post-close cooldown stamp. All
 *  mutation is synchronous field assignment (mirrors the plain-ref style the
 *  rest of the file uses) — held in ONE useRef in useVoiceController.ts
 *  instead of five. */
export class ConversationTurn {
  /** CLARIFY / PUBLISH_CONFIRM sub-state, or null for a plain turn. */
  subState: TurnSubState = null;
  /** true for the WHOLE hands-free wake turn (initial command capture + any
   *  clarify follow-up leg); false for PTT and for a settled/closed turn. */
  handsFreeTurn = false;
  /** false until the FIRST wake greeting has actually played this session;
   *  thereafter a wake speaks the "anything else?" follow-up instead. Reset to
   *  false by close() so the NEXT wake (after the cooldown) greets fresh. */
  hasWoken = false;
  /** wall-clock ms of the last explicit turn close, or null before any close
   *  this session. Entity-owned per §3 ("the 9s post-close cooldown becomes
   *  an entity-owned property"). */
  closedAtMs: number | null = null;

  /** TRUE while Aura's own TTS audio is ACTUALLY playing -- set by
   *  beginAnswering()/endAnswering(), bound to the REAL onStart/onEnded
   *  playback callbacks (never a bare VoiceStatus flip). Phase C / scope doc
   *  §3: the wake reducer's re-arm gate reads this (via isAnsweringOrGrace),
   *  not VoiceStatus -- closes the self-trigger TTS-bleed class. */
  answering = false;
  /** wall-clock ms of the last endAnswering() call, or null before any
   *  answer has played this session. Feeds the post-playback grace window. */
  answeringEndedAtMs: number | null = null;

  /** F3 follow-up cap (10-10-2026): "Is there anything else?" prompts actually
   *  PLAYED since the last close(). Read by selectWakePrompt; reset by close(). */
  followUpPromptsSpoken = 0;
  /** F3: TRUE for a wake turn whose prompt was suppressed by the cap (capture
   *  opened silently). A no-request capture in such a turn closes quietly
   *  ('quiet_close') instead of the ask-to-repeat reprompt. */
  quietTurn = false;

  /** F3: the follow-up prompt really played (call only on a 'played' outcome). */
  noteFollowUpPromptPlayed(): void {
    this.followUpPromptsSpoken += 1;
  }

  /** Start a fresh hands-free wake turn: enables the briefing-intent trigger
   *  and drops any stale sub-state from a prior turn (IVG #2.3-DF3). Does NOT
   *  touch hasWoken/closedAtMs — those are session/cooldown concerns, not
   *  turn-start concerns. */
  startWakeTurn(quiet = false): void {
    this.handsFreeTurn = true;
    this.subState = null;
    this.quietTurn = quiet;
  }

  /** A manual press-to-talk takes OWNERSHIP of the turn (IVG F3): PTT speech
   *  must never route into a pending hands-free clarify/publish-confirm
   *  sub-state or inherit hands-free routing. */
  resetForManualTurn(): void {
    this.subState = null;
    this.handsFreeTurn = false;
    this.quietTurn = false;
  }

  /** The clarify follow-up leg's capture failed to open (D1 fix): abandon the
   *  clarify sub-state and end the hands-free turn so the controller's
   *  full-briefing fallback is the ONLY thing that happens next. */
  abandonClarify(): void {
    this.subState = null;
    this.handsFreeTurn = false;
    this.quietTurn = false;
  }

  /** The wake turn settled normally (no clarify follow-up opened). */
  endWakeTurn(): void {
    this.handsFreeTurn = false;
    this.quietTurn = false;
  }

  setClarify(modules: BriefingModule[], utterance: string): void {
    this.subState = { kind: 'clarify', modules, reAskCount: 0, utterance };
  }

  setPublishConfirm(draft: PublishConfirmDraft): void {
    this.subState = { kind: 'publish_confirm', draft, reAskCount: 0 };
  }

  setBatchPublish(targetId: number, draftIds: number[], channel: BatchChannel, readbackMode: BatchReadbackMode | null = null): void {
    this.subState = { kind: 'batch_publish', targetId, draftIds: [...draftIds], channel, reAskCount: 0, readbackMode };
  }

  setSuggestWrite(targetId: number, channel: BatchChannel, pick: number | null = null): void {
    this.subState = { kind: 'suggest_write', targetId, channel, reAskCount: 0, pick };
  }

  setRecommend(channel: BatchChannel): void {
    this.subState = { kind: 'recommend', channel, reAskCount: 0 };
  }

  /** Mark the open suggest_write question as "which ones?" (keeps the re-ask count). */
  markSuggestSelecting(): void {
    if (this.subState?.kind === 'suggest_write') this.subState = { ...this.subState, selecting: true, pick: null };
  }

  /** Bump the current sub-state's re-ask counter in place. Returns the NEW
   *  count (0 if there is no current sub-state — defensive; call sites only
   *  invoke this from within their own kind-gated branch). */
  incReask(): number {
    if (this.subState === null) return 0;
    this.subState.reAskCount += 1;
    return this.subState.reAskCount;
  }

  clearSubState(): void {
    this.subState = null;
  }

  /** Clear the sub-state ONLY if it is currently the given kind — preserves
   *  the exact pre-Phase-B behaviour of stop()/cancelToTalk(), which cleared
   *  publishConfirmCtxRef alone and deliberately left a hands-free clarify
   *  sub-state untouched (an existing, out-of-scope latent behaviour Phase B
   *  must not silently change — see the Phase B build note). */
  clearSubStateIfKind(kind: 'clarify' | 'publish_confirm' | 'batch_publish' | 'suggest_write' | 'recommend'): void {
    if (this.subState?.kind === kind) this.subState = null;
  }

  markWoken(): void {
    this.hasWoken = true;
  }

  /** Aura's own TTS audio has ACTUALLY started playing -- call ONLY from a
   *  real playback-start callback (onStart), never from a request/dispatch
   *  alone (Phase C). */
  beginAnswering(): void {
    this.answering = true;
    this.answeringEndedAtMs = null;
  }

  /** Aura's own TTS audio has ACTUALLY finished -- call ONLY from a real
   *  playback-ended callback (AuraAudioPlayer's real 'ended' event /
   *  BufferSource onended via SegmentedNarrator's onEnded, never a coarse
   *  VoiceStatus flip). Stamps the moment so isAnsweringOrGrace() can hold
   *  the scorer off for a short grace window afterwards (Phase C). */
  endAnswering(nowMs: number): void {
    this.answering = false;
    this.answeringEndedAtMs = nowMs;
  }

  /** TRUE while audio is physically in flight OR within graceMs of it ending
   *  -- the wake reducer's ONE source of truth for "is it safe to re-arm
   *  scoring", replacing a bare VoiceStatus==='idle' read (Phase C / the
   *  self-trigger fix, scope doc §3: "IDLE(armed)/scoring-active is only
   *  entered from a confirmed ANSWERING->CLOSING transition"). */
  isAnsweringOrGrace(nowMs: number, graceMs: number = POST_ANSWER_SCORE_GRACE_MS): boolean {
    if (this.answering) return true;
    return this.answeringEndedAtMs !== null && nowMs - this.answeringEndedAtMs < graceMs;
  }

  /** TRUE while a wake fire is inside the post-close cooldown window — a
   *  marginal re-fire seconds after an explicit close, not a genuine new
   *  "Hey Aura" (D4 fix). */
  isInCooldown(nowMs: number): boolean {
    return this.closedAtMs !== null && nowMs - this.closedAtMs < POST_CLOSE_COOLDOWN_MS;
  }

  /** THE single CLOSING transition (§3 / Phase B item 2): resets EVERYTHING a
   *  turn-close owns in ONE place — hasWoken, handsFreeTurn, the sub-state,
   *  and the cooldown stamp — replacing wave-4's handleTurnCloseWake five-line
   *  manual reset. */
  close(nowMs: number): void {
    this.hasWoken = false;
    this.closedAtMs = nowMs;
    this.handsFreeTurn = false;
    this.subState = null;
    this.followUpPromptsSpoken = 0; // F3: the next conversation may prompt once again
    this.quietTurn = false;
  }

  /** THE per-state onFinal routing dispatcher (§3 / Phase B item 3). Pure:
   *  reads only `this.subState`/`this.handsFreeTurn` and the transcript.
   *  Byte-identical decision order to the pre-Phase-B inline chain — see the
   *  Phase B build note's behaviour-preservation table. */
  classifyFinal(transcript: string): FinalRoute {
    // Sub-state-first dispatch (§3 "per-state routing"): a PUBLISH_CONFIRM
    // sub-state ALWAYS wins (closed-set yes/edit/no answers are frequently
    // one word — must never be mistaken for a turn-close/fragment/command).
    if (this.subState?.kind === 'publish_confirm') return { kind: 'publish_confirm_answer' };
    // Aura publish P1: while the batch cards wait for "publish"/"draft", the
    // answer ALWAYS goes to the closed-set matcher (one-word answers, and its own
    // NO set covers close/stop), exactly like publish_confirm above.
    if (this.subState?.kind === 'batch_publish') return { kind: 'batch_publish_answer' };
    // Aura P2: while the suggestion cards wait for "yes" / "write them", the answer
    // ALWAYS goes to its closed-set matcher (its own NO set covers close/stop).
    if (this.subState?.kind === 'suggest_write') return { kind: 'suggest_write_answer' };
    // conversational-actions: the recommend list's open question takes the next answer
    // (its handler owns "no"/turn-close, like the batch/suggest answers above).
    if (this.subState?.kind === 'recommend') return { kind: 'recommend_answer' };

    // TURN-CLOSE: evaluated before the fragment guard (a close phrase is
    // often a single word) and NEVER while a CLARIFY sub-state is open (a
    // clarify answer is evaluated on its own terms below, even if it happens
    // to overlap the close vocabulary — unchanged from the pre-Phase-B rule).
    if (this.handsFreeTurn && this.subState?.kind !== 'clarify' && isTurnClose(transcript)) {
      return { kind: 'turn_close' };
    }
    // FOLLOW-UP DECLINE (owner directive 10-10-2026 ~15:03 UTC: "also include
    // words like 'no' etc when asked is there anything else"): while the turn is
    // answering Aura's own open prompt ("How can I help?" / "Is there anything
    // else?") -- hands-free, NO clarify/publish-confirm sub-state -- a short
    // decline ("I'm fine", "not now", "no, that's fine") closes like "no".
    // Clarify keeps plain "no" as a menu answer (unchanged, below).
    if (this.handsFreeTurn && this.subState === null && isFollowUpDecline(transcript)) {
      return { kind: 'turn_close' };
    }
    // F2 (owner GO 10-10-2026): a STRONG stop ("shut up", "cancel", "never mind",
    // "no thanks"...) also closes while the "which module?" CLARIFY sub-state is
    // open. A plain "no" / "nothing" stays a clarify answer (re-ask policy), and
    // the menu's own "all" wording ("that's all") keeps its full-briefing
    // meaning (isStrongStop inClarify=true excludes CLARIFY_AMBIGUOUS_STOPS).
    if (this.handsFreeTurn && this.subState?.kind === 'clarify' && isStrongStop(transcript, true)) {
      return { kind: 'turn_close' };
    }

    // CLARIFY-ANSWER: a non-empty transcript while a CLARIFY sub-state is
    // open is routed to the matcher before the fragment guard (single-word
    // answers like "SEO"/"Sales"/"all" must not be rejected as fragments).
    if (this.subState?.kind === 'clarify' && transcript.trim() !== '') {
      return { kind: 'clarify_answer' };
    }

    // FRAGMENT / empty / low-quality transcript.
    if (finalTranscriptAction(transcript) === 'reprompt') {
      // A no-answer DURING a clarify must not exit silently — full-briefing
      // fallback (the entity's clarify no-answer policy), never a dead end.
      if (this.subState?.kind === 'clarify') return { kind: 'clarify_no_answer_fallback' };
      // F3: a capped (silent) follow-up wake with no real request closes
      // quietly -- never the ask-to-repeat re-prompt.
      if (this.quietTurn && this.handsFreeTurn) return { kind: 'quiet_close' };
      return { kind: 'fragment_reprompt' };
    }

    // Aura P2: "which 3 articles should we write (for my top keywords)?" — PTT AND
    // hands-free. Its detector never matches a push/publish ask (that is P1 below).
    const suggestAsk = detectSuggestWriteAsk(transcript);
    if (suggestAsk) return { kind: 'suggest_write_ask', ask: suggestAsk };

    // Aura publish P1: "push the next N drafts (to <site>)" — PTT AND hands-free.
    const batchAsk = detectBatchPublishAsk(transcript);
    if (batchAsk) return { kind: 'batch_publish_ask', ask: batchAsk };

    // conversational-actions: "what do you recommend?" / "show me my pending blogs" ->
    // the numbered list (checked after the explicit write/push asks, before any model).
    const recAsk = detectRecommendAsk(transcript) ?? detectPendingListAsk(transcript);
    if (recAsk) return { kind: 'recommend_ask', ask: recAsk };

    // Hands-free-only routing (PTT never reaches here with handsFreeTurn set).
    if (this.handsFreeTurn) {
      const moduleAsk = detectModuleAsk(transcript);
      if (moduleAsk) return { kind: 'module_ask', keys: moduleAsk };
      if (isBriefingIntent(transcript)) return { kind: 'briefing' };
    }

    return { kind: 'normal' };
  }
}
